import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { Close, Save } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import { fetchRoles, saveUserInformation } from "../../../api_calls/userManagement";
import { loadRetailerSelectionOptions } from "../../../api_calls/retailerSelection";
import { toast } from "react-toastify";
import GroupedMultiSelect from "../../../components/commonComponents/Selectors/GroupedMultiSelect";
import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";

const AddTenantUser = ({ open, onClose, size, usedFor, userInfo, closeModal }) => {
  const [selectedRolePermissions, setSelectedRolePermissions] = React.useState(
    usedFor === "update" && Array.isArray(userInfo.roles)
      ? userInfo.roles.map((roleName) => {
          return { value: roleName, label: roleName };
        })
      : []
  );
  const [userDetails, setUserDetails] = React.useState(userInfo);
  const [fieldError, setFieldError] = React.useState({});
  // Retailer roots returned for this viewer. A shorter list for a limited tenant comes from the API.
  const [retailerOptions, setRetailerOptions] = React.useState([]);
  const [selectedRetailerOptions, setSelectedRetailerOptions] = React.useState([]);
  const [retailersReady, setRetailersReady] = React.useState(false);

  const currentStateData = useSelector((state) => state.userSelections);
  const roleAndPermissions = typeof currentStateData.role_list !== "undefined" ? currentStateData.role_list : [];

  const rolesList =
    roleAndPermissions.length !== 0
      ? roleAndPermissions
        .map((roleObj) => {
          return {
            value: roleObj.role_name,
            label: roleObj.role_name,
            deactivated: roleObj.deactivated,
          };
        })
        .filter((role) => role.deactivated !== true)
      : [];

  const dispatchAction = useDispatch();

  const handleFieldChange = (user_data) => {
    setUserDetails((previousDetails) => {
      const updatedDetails = {
        ...previousDetails,
        ...user_data,
      };
      dispatchAction(updateStateData(updatedDetails));
      return updatedDetails;
    });
  };

  // retailer_ids, then the Retailer column, then assigned companies. Later sources are used only when the earlier one is missing.
  const retailerIdsOnUser = (user) => {
    if (Array.isArray(user?.retailer_ids)) {
      return user.retailer_ids;
    }
    if (Array.isArray(user?.retailers)) {
      return user.retailers.map((retailer) => retailer.id);
    }
    if (Array.isArray(user?.assigned_companies)) {
      return user.assigned_companies.map((company) => company.id);
    }
    return [];
  };

  // selected_company_ids is what the tenant save sends. Ids absent from the option list are left off.
  const applyRetailerSelection = (selectedOptions) => {
    setSelectedRetailerOptions(selectedOptions);
    if (selectedOptions.length !== 0 && typeof fieldError.retailer !== "undefined") {
      setFieldError((previousErrors) => {
        const { retailer, ...remainingErrors } = previousErrors;
        return remainingErrors;
      });
    }
    handleFieldChange({
      selected_company_ids: selectedOptions.map((option) => option.id),
    });
  };

  React.useEffect(() => {
    fetchRoles(false, { type: "tenant" });
    // Clear any retailer selection left in Redux by a previous user form before this list loads.
    handleFieldChange({ ...userInfo, cyreen_user: false, selected_company_ids: [] });

    const loadRetailerOptions = async () => {
      const companiesInfo = await loadRetailerSelectionOptions("tenant_user_company_options", {
        includeAllRetailers: false,
        persistSelection: false,
      });
      const options = companiesInfo.success === true && Array.isArray(companiesInfo.options)
        ? companiesInfo.options
        : [];
      setRetailerOptions(options);

      const assignedIds = new Set(retailerIdsOnUser(userInfo).map((id) => String(id)));
      let selectedOptions = [];
      if (usedFor === "update") {
        selectedOptions = options.filter((option) => assignedIds.has(String(option.id)));
      } else if (options.length === 1) {
        // One retailer means there is nothing to choose, so the field starts selected.
        selectedOptions = [options[0]];
      }
      applyRetailerSelection(selectedOptions);
      setRetailersReady(true);
    };

    loadRetailerOptions();

    if (usedFor === "update" && Array.isArray(userInfo.roles)) {
      setSelectedRolePermissions(
        userInfo.roles.map((roleName) => {
          return { value: roleName, label: roleName };
        })
      );
    }
  }, []);

  React.useEffect(() => {
    if (usedFor === "update" || selectedRolePermissions.length !== 0 || rolesList.length !== 1) {
      return;
    }

    const autoSelectedRole = rolesList[0];
    setSelectedRolePermissions([autoSelectedRole]);
    handleFieldChange({
      roles: [autoSelectedRole.label],
    });
  }, [roleAndPermissions]);

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.preventDefault();
      }}
      fullWidth={true}
      maxWidth={size}
    >
      <DialogTitle>{usedFor === "update" ? "Update User" : "Add New User"}</DialogTitle>
      <DialogContent>
        <Box>
          <Stack direction={"row"} spacing={4}>
            <TextInputField
              fieldRequired={true}
              readOnlyFlag={usedFor === "update" ? true : false}
              elemId="userName"
              label="User Name"
              fieldName="user_name"
              showlabel={true}
              defaultVal={typeof userDetails.username !== "undefined" ? userDetails.username : ""}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                const value = e.target.value;
                if (value.includes(" ")) {
                  setFieldError({ ...fieldError, user_name: "Username must not contain spaces" });
                } else if (fieldError.user_name) {
                  const { user_name, ...rest } = fieldError;
                  setFieldError(rest);
                }
                handleFieldChange({
                  username: value.replace(/\s/g, ""),
                });
              }}
              fieldError={
                typeof fieldError.user_name !== "undefined" && fieldError.user_name !== "" ? fieldError.user_name : ""
              }
            />
            <TextInputField
              fieldRequired={true}
              elemId="email"
              readOnlyFlag={usedFor === "update" ? true : false}
              label="E-Mail"
              fieldName="email"
              showlabel={true}
              defaultVal={typeof userDetails.email !== "undefined" ? userDetails.email : ""}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                const value = e.target.value;
                if (value.includes(" ")) {
                  setFieldError({ ...fieldError, email: "E-Mail must not contain spaces" });
                } else if (fieldError.email) {
                  const { email, ...rest } = fieldError;
                  setFieldError(rest);
                }
                handleFieldChange({
                  email: value.replace(/\s/g, ""),
                });
              }}
              fieldError={typeof fieldError.email !== "undefined" && fieldError.email !== "" ? fieldError.email : ""}
            />
          </Stack>

          <Box height={40} />
          {/* Retailer is chosen before the role. Search stays off; MultiSelect sorts A–Z and keeps Select All. */}
          <Stack direction={"row"} spacing={4}>
            <MultiSelect
              label="Retailer*"
              elemId="tenantRetailerDropdown"
              enableFullWidth={true}
              disableSearch={true}
              options={retailerOptions}
              defaultVal={selectedRetailerOptions}
              selectionCheck={(option, value) => option.id === value.id}
              onChangeCall={(selectedOptions) => {
                applyRetailerSelection(selectedOptions);
              }}
              fieldError={
                typeof fieldError.retailer !== "undefined" && fieldError.retailer !== ""
                  ? fieldError.retailer
                  : ""
              }
            />
          </Stack>
          <Box height={40} />
          <Stack direction={"row"} spacing={4}>
            <GroupedMultiSelect
              enableFullWidth={true}
              elemId={"selectRoleDropdown"}
              label={"User role*"}
              showSelectAll={false}
              options={
                rolesList.length !== 0
                  ? [...rolesList].sort((a, b) => {
                      return a.label.localeCompare(b.label);
                    })
                  : []
              }
              showGroupNames={false}
              selectionCheck={(option, value) => option.label === value.label}
              defaultVal={selectedRolePermissions}
              onChangeCall={(selectedOption) => {
                const roleLabels = selectedOption.map((roleDtl) => roleDtl.label);
                setSelectedRolePermissions(selectedOption);
                handleFieldChange({
                  roles: roleLabels,
                });
              }}
              fieldError={
                typeof fieldError.user_role !== "undefined" && fieldError.user_role !== "" ? fieldError.user_role : ""
              }
            />
          </Stack>
          <Box height={40} />
        </Box>
      </DialogContent>
      <Box height={20} />
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <CloseButton onClick={onClose} label="Cancel" icon={<Close />} />
          <ActionButton
            // Wait until retailer roots have loaded so a single-retailer account is preselected before save.
            disabled={retailersReady !== true}
            onClick={(e) => {
              e.preventDefault();
              setFieldError({});

              const userId = { id: typeof userDetails.id !== "undefined" ? userDetails.id : "" };
              saveUserInformation(usedFor, userId, "tenant").then((info) => {
                if (info.success === true) {
                  const message = usedFor === "update" ? "User updated successfully" : "User added successfully";
                  toast(message, { type: "success" });
                  closeModal();
                } else {
                  setFieldError(info.errorMessages);
                }
              });
            }}
            label={usedFor === "update" ? "Update" : "Save"}
            icon={<Save />}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddTenantUser;
